import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  AlertTitle,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  CircularProgress,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { post } from '../../utils/api';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const rowKey = (m) => `${m.date}|${m.name}|${m.amount}`;

// Read a File as a base64 data URL (the backend strips the "data:application/pdf;base64," prefix).
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });
}

export default function ZelleReconciliationDialog({ open, onClose }) {
  const [fileName, setFileName] = React.useState('');
  const [dataUrl, setDataUrl] = React.useState('');
  const [checking, setChecking] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null); // { year, summary, missing[] }
  const [selected, setSelected] = React.useState(() => new Set());
  const [outcomes, setOutcomes] = React.useState(() => ({})); // rowKey -> { status, woo_order_id, error, existing_order_id }
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [debugText, setDebugText] = React.useState(''); // extracted-text sample shown when 0 deposits parsed
  const fileInputRef = React.useRef(null);

  const reset = React.useCallback(() => {
    setFileName(''); setDataUrl(''); setError(''); setResult(null);
    setSelected(new Set()); setOutcomes({}); setChecking(false); setCreating(false); setConfirmOpen(false); setDebugText('');
  }, []);

  const handleClose = () => { if (!checking && !creating) { reset(); onClose && onClose(); } };

  const onPickFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(''); setResult(null); setSelected(new Set()); setOutcomes({});
    if (file.type && file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
      setError('Please choose a PDF bank statement.'); return;
    }
    try {
      const url = await fileToDataUrl(file);
      setDataUrl(url);
      setFileName(file.name);
    } catch (err) {
      setError(err.message || 'Could not read the file');
    }
  };

  const runCheck = async () => {
    if (!dataUrl) { setError('Choose a PDF first.'); return; }
    setChecking(true); setError(''); setResult(null); setSelected(new Set()); setOutcomes({}); setDebugText('');
    try {
      const res = await post('/api/manage/zelle-reconcile', { pdf_base64: dataUrl }, { timeout: 120000 });
      setResult(res);
      // Pre-select all missing rows for convenience.
      setSelected(new Set((res.missing || []).map(rowKey)));
    } catch (err) {
      setError(err?.message || 'Reconciliation failed');
      // When no deposits were parsed, the backend returns a sample of the extracted text so we can see
      // the actual PDF layout and tune the parser.
      if (err?.data?.text_sample) setDebugText(String(err.data.text_sample));
    } finally {
      setChecking(false);
    }
  };

  const missing = (result && result.missing) || [];
  const selectableKeys = missing.filter((m) => !outcomes[rowKey(m)] || outcomes[rowKey(m)].status === 'error').map(rowKey);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const someSelected = selectableKeys.some((k) => selected.has(k));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) { selectableKeys.forEach((k) => next.delete(k)); }
      else { selectableKeys.forEach((k) => next.add(k)); }
      return next;
    });
  };
  const toggleOne = (k) => {
    setSelected((prev) => { const next = new Set(prev); if (next.has(k)) next.delete(k); else next.add(k); return next; });
  };

  const selectedRows = missing.filter((m) => selected.has(rowKey(m)));
  const selectedTotal = selectedRows.reduce((s, m) => s + Number(m.amount || 0), 0);

  const doCreate = async () => {
    setConfirmOpen(false);
    if (!selectedRows.length) return;
    setCreating(true); setError('');
    try {
      const res = await post('/api/manage/zelle-reconcile/create', {
        year: result.year,
        deposits: selectedRows.map((m) => ({ date: m.date, name: m.name, amount: m.amount })),
      }, { timeout: 300000 });
      const map = { ...outcomes };
      (res.results || []).forEach((r) => { map[`${r.date}|${r.name}|${r.amount}`] = r; });
      setOutcomes(map);
      // Deselect the rows we just processed successfully (created/skipped); leave errors selected for retry.
      setSelected((prev) => {
        const next = new Set(prev);
        (res.results || []).forEach((r) => { if (r.status !== 'error') next.delete(`${r.date}|${r.name}|${r.amount}`); });
        return next;
      });
    } catch (err) {
      setError(err?.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const createdCount = Object.values(outcomes).filter((o) => o.status === 'created').length;

  const renderOutcome = (m) => {
    const o = outcomes[rowKey(m)];
    if (!o) return <Typography variant="body2" color="text.secondary">—</Typography>;
    if (o.status === 'created') {
      return (
        <Chip size="small" color="success" icon={<CheckCircleIcon />} label={`Woo #${o.woo_order_id}`} />
      );
    }
    if (o.status === 'skipped') {
      return (
        <Tooltip title={o.existing_order_id ? `Matched existing order #${o.existing_order_id}` : 'Already exists'}>
          <Chip size="small" color="default" label={o.existing_order_id ? `Exists #${o.existing_order_id}` : 'Already exists'} />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={o.error || 'Failed'}>
        <Chip size="small" color="error" icon={<ErrorOutlineIcon />} label="Failed" />
      </Tooltip>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Zelle Reconciliation
        <IconButton onClick={handleClose} disabled={checking || creating} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a Truist bank Zelle statement (PDF). We compare each incoming Zelle deposit against
          Woo orders (matching on the order&apos;s <em>Sent by</em> name, amount and date) and list any
          deposits with no matching order. Select the ones to post and we&apos;ll create the Woo orders.
        </Typography>

        {/* Upload row */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <input ref={fileInputRef} type="file" accept="application/pdf" hidden onChange={onPickFile} />
          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={checking || creating}>
            {fileName ? 'Change PDF' : 'Choose PDF'}
          </Button>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fileName || 'No file selected'}
          </Typography>
          <Button variant="contained" onClick={runCheck} disabled={!dataUrl || checking || creating}>
            {checking ? <CircularProgress size={20} /> : 'Check for missing'}
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {debugText && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Extracted text (first {debugText.length} chars) — for parser diagnostics:
            </Typography>
            <Box
              component="pre"
              sx={{
                mt: 0.5, p: 1, maxHeight: 260, overflow: 'auto', bgcolor: 'grey.100',
                border: '1px solid', borderColor: 'divider', borderRadius: 1,
                fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}
            >
              {debugText}
            </Box>
          </Box>
        )}

        {result && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip label={`Year ${result.year}`} size="small" />
              <Chip label={`${result.summary.deposits} deposits`} size="small" />
              <Chip label={`${result.summary.matched} matched`} color="success" size="small" variant="outlined" />
              <Chip label={`${result.summary.missing} missing`} color={result.summary.missing ? 'warning' : 'default'} size="small" />
              <Chip label={`${money(result.summary.missing_total)} missing`} color={result.summary.missing ? 'warning' : 'default'} size="small" variant="outlined" />
            </Stack>

            {missing.length === 0 ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                All Zelle deposits in this statement have matching Woo orders. Nothing to create.
              </Alert>
            ) : (
              <>
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 1 }}>
                  Creating writes <strong>real Woo orders</strong> (payment method Zelle, backdated to the
                  deposit date). <strong>No emails are sent.</strong> A duplicate check runs again per order
                  just before it&apos;s created.
                </Alert>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={someSelected && !allSelected}
                            checked={allSelected}
                            onChange={toggleAll}
                            disabled={creating || selectableKeys.length === 0}
                          />
                        </TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Sent by</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {missing.map((m) => {
                        const k = rowKey(m);
                        const o = outcomes[k];
                        const locked = o && o.status !== 'error';
                        return (
                          <TableRow key={k} hover selected={selected.has(k)}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selected.has(k)}
                                onChange={() => toggleOne(k)}
                                disabled={creating || locked}
                              />
                            </TableCell>
                            <TableCell>{m.date}</TableCell>
                            <TableCell>{m.name}</TableCell>
                            <TableCell align="right">{money(m.amount)}</TableCell>
                            <TableCell>{renderOutcome(m)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </>
        )}

        {createdCount > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Created {createdCount} Woo order{createdCount === 1 ? '' : 's'} this session.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={checking || creating}>Close</Button>
        {missing.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setConfirmOpen(true)}
            disabled={creating || selectedRows.length === 0}
          >
            {creating ? <CircularProgress size={20} /> : `Create ${selectedRows.length} Woo order${selectedRows.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>

      {/* Confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create {selectedRows.length} Woo order{selectedRows.length === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>This posts real donations to WooCommerce</AlertTitle>
            {selectedRows.length} order{selectedRows.length === 1 ? '' : 's'} totaling <strong>{money(selectedTotal)}</strong>,
            backdated to each deposit date. No donor emails are sent.
          </Alert>
          <Typography variant="caption" color="text.secondary">
            Tip: try one first to confirm it lands correctly, then do the rest.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={doCreate}>Create orders</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
